import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { productsAPI } from '../lib/api';
import { AlertTriangle, Package } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Product {
    id: string;
    _id: string;
    name: string;
    stock: number;
    category?: string;
}

const Inventory = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await productsAPI.getAll();
            setProducts(response.data);
        } catch (error) {
            toast.error('Failed to load inventory');
        } finally {
            setLoading(false);
        }
    };

    const lowStockProducts = products.filter(p => p.stock < 10);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Inventory</h1>
                <p className="text-gray-500">Monitor stock levels</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Package className="text-blue-600" size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-gray-800">{products.length}</p>
                            <p className="text-sm text-gray-500">Total Products</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                            <AlertTriangle className="text-red-600" size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-gray-800">{lowStockProducts.length}</p>
                            <p className="text-sm text-gray-500">Low Stock</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Low Stock Alert */}
            {lowStockProducts.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                    <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                        <AlertTriangle size={20} />
                        Low Stock Alert
                    </h3>
                    <div className="space-y-2">
                        {lowStockProducts.map((product) => (
                            <div key={product.id || product._id} className="flex justify-between text-sm">
                                <span className="text-red-700">{product.name}</span>
                                <span className="font-medium text-red-800">{product.stock} left</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* All Products */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b">
                    <h2 className="font-semibold text-gray-800">All Products</h2>
                </div>
                <div className="divide-y">
                    {products.map((product) => (
                        <div key={product.id || product._id} className="p-4 flex justify-between items-center">
                            <div>
                                <p className="font-medium text-gray-800">{product.name}</p>
                                <p className="text-xs text-gray-500">{product.category}</p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-medium ${product.stock === 0
                                ? 'bg-red-100 text-red-800'
                                : product.stock < 10
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                {product.stock} in stock
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
};

export default Inventory;